import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Vacancy, Client, VacancyStep, UserRole } from '../types';
import { Sparkles, Check, TrendingUp, Settings, Loader2, Building2, Users, Briefcase, Phone, MessageCircle, Calendar, Search, Plus, Wand2, X } from 'lucide-react';
import { supabase_update, supabase_get_clients, supabase } from '../services/supabaseService';
import { generateCompanyDescription } from '../services/geminiService';
import { appendVacancyFlowStepHistory, type VacancyFlowActor } from '../utils/vacancyFlowHistory';
import { describeVacancySnapshotChanges, type VacancySnapshotForDiff } from '../utils/vacancyChangeDiff';
import SupportSchedulerModal from './SupportSchedulerModal';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { nl } from 'date-fns/locale';

registerLocale('nl', nl);

const formatDateTimeString = (date: Date | null) => {
    if (!date) return '';
    return date.toISOString();
};

const parseDateString = (dateStr: string) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

const VACANCY_ROLES = [
  { id: 'Vacature Owner', desc: 'Eindverantwoordelijke' },
  { id: 'Recruiter', desc: 'Uitvoering werving' },
  { id: 'Sourcer', desc: 'Kandidaten zoeken' },
  { id: 'Hiring Manager', desc: 'Inhoudelijke beoordeling' },
  { id: 'Viewer', desc: 'Alleen meekijken' },
  { id: 'Client User', desc: 'Klantportaal' },
  { id: 'Werfplaats Recruiter', desc: 'Externe recruiter' }
];

interface VacancyTeamChoiceProps {
    vacancy: Vacancy;
    onUpdate: (updates: Partial<Vacancy>) => void;
    onNext: () => void;
    isReadOnly?: boolean;
    onOpenTeamModal?: () => void;
    actorUser?: { id?: string; name?: string; email?: string };
    userRole?: UserRole | string;
}

const VacancyTeamChoice: React.FC<VacancyTeamChoiceProps> = ({ vacancy, onUpdate, onNext, isReadOnly = false, onOpenTeamModal, actorUser, userRole }) => {
    const [formData, setFormData] = useState({
        clientId: vacancy.clientId || '',
        sharedWithUsers: vacancy.shared_with_users || [],
        sharedWithClients: vacancy.shared_with_clients || [],
        askWerfplaats: (vacancy.data as any)?.askWerfplaats || false,
        startDate: vacancy.data?.startDate || '',
        closingDate: vacancy.data?.closingDate || '',
        planningDate: vacancy.data?.planningDate || '',
        urgencyLevel: vacancy.data?.urgencyLevel || 'Medium Priority',
        campaignChoice: vacancy.data?.campaignChoice || 'werfplaats',
        werfplaatsTeamConfig: vacancy.data?.werfplaatsTeamConfig || { leads: 50, selection: 'Alle profielen', interview: 'Zelf Doen', support: 'Geen Hulp' },
        teamRoles: (vacancy.data as any)?.teamRoles || {},
        externalReviewers: (vacancy.data as any)?.externalReviewers || []
    });
    const [isSaving, setIsSaving] = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    const [clients, setClients] = useState<Client[]>([]);
    const [colleagues, setColleagues] = useState<any[]>([]);
    const [contactSuccess, setContactSuccess] = useState<string | null>(null);
    const [showScheduler, setShowScheduler] = useState(false);
    
    // Client Dropdown State
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [clientSearchText, setClientSearchText] = useState('');
    const clientDropdownRef = React.useRef<HTMLDivElement>(null);

    // New Company Modal State
    const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
    const [newCompanyForm, setNewCompanyForm] = useState({ name: '', website: '', profile: '', description: '' });
    const [isGeneratingAi, setIsGeneratingAi] = useState(false);
    const [isSavingNewCompany, setIsSavingNewCompany] = useState(false);

    // New Team Inline Search
    const [teamSearch, setTeamSearch] = useState('');
    const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
    const teamDropdownRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutsideTeam = (event: MouseEvent) => {
            if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
                setIsTeamDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideTeam);
        return () => document.removeEventListener('mousedown', handleClickOutsideTeam);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
                setIsClientDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        supabase_get_clients().then(data => setClients(data || []));
    }, []);

    useEffect(() => {
        const fetchColleagues = async () => {
            let query = supabase.from('profiles').select('id, name, email, role');
            if (formData.clientId) {
                query = query.eq('client_id', formData.clientId);
            }
            const { data } = await query;
            if (data) setColleagues(data);
        };
        fetchColleagues();
    }, [formData.clientId]);

    // Sync formData with external changes (e.g. from ShareModal in VacancyFlow if still used)
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            sharedWithUsers: vacancy.shared_with_users || [],
            sharedWithClients: vacancy.shared_with_clients || [],
            teamRoles: (vacancy.data as any)?.teamRoles || prev.teamRoles,
            externalReviewers: (vacancy.data as any)?.externalReviewers || prev.externalReviewers
        }));
    }, [JSON.stringify(vacancy.shared_with_users), JSON.stringify(vacancy.shared_with_clients)]);

    const addUserToTeam = (userId: string, isAgency = false) => {
        if (isAgency) {
            if (!formData.sharedWithClients.includes(userId)) {
                handleChange('sharedWithClients', [...formData.sharedWithClients, userId]);
            }
        } else {
            if (!formData.sharedWithUsers.includes(userId)) {
                handleChange('sharedWithUsers', [...formData.sharedWithUsers, userId]);
                handleChange('teamRoles', { ...formData.teamRoles, [userId]: 'Recruiter' });
            }
        }
        setTeamSearch('');
        setIsTeamDropdownOpen(false);
    };

    const addExternalReviewer = (email: string) => {
        if (!formData.externalReviewers.includes(email)) {
            handleChange('externalReviewers', [...formData.externalReviewers, email]);
        }
        setTeamSearch('');
        setIsTeamDropdownOpen(false);
    };

    const removeExternalReviewer = (email: string) => {
        handleChange('externalReviewers', formData.externalReviewers.filter((e: string) => e !== email));
    };

    const addAllColleagues = () => {
        const newSharedUsers = [...new Set([...formData.sharedWithUsers, ...colleagues.map(c => c.id)])];
        const newRoles = { ...formData.teamRoles };
        colleagues.forEach(c => {
            if (!newRoles[c.id]) newRoles[c.id] = 'Recruiter';
        });
        setFormData(prev => ({ ...prev, sharedWithUsers: newSharedUsers, teamRoles: newRoles }));
    };

    const removeUserFromTeam = (userId: string, isAgency = false) => {
        if (isAgency) {
            handleChange('sharedWithClients', formData.sharedWithClients.filter(id => id !== userId));
        } else {
            handleChange('sharedWithUsers', formData.sharedWithUsers.filter(id => id !== userId));
            const newRoles = { ...formData.teamRoles };
            delete newRoles[userId];
            handleChange('teamRoles', newRoles);
        }
    };

    const handleContactWerfplaats = (method: string) => {
        if (method === 'schedule') {
            setShowScheduler(true);
            return;
        }

        setContactSuccess(method);
        if (method === 'whatsapp') {
            window.open('https://wa.me/31628114008?text=Hoi%20Werfplaats,%20ik%20heb%20hulp%20nodig%20bij%20vacature:%20' + encodeURIComponent(vacancy.title));
        }
        
        setTimeout(() => setContactSuccess(null), 3000);
    };

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleTeamConfigChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            werfplaatsTeamConfig: {
                ...prev.werfplaatsTeamConfig,
                [field]: value
            }
        }));
    };

    const handleSave = async (proceed: boolean) => {
        setIsSaving(true);
        const updates = {
            shared_with_users: formData.sharedWithUsers,
            shared_with_clients: formData.sharedWithClients,
            data: {
                ...vacancy.data,
                askWerfplaats: formData.askWerfplaats,
                startDate: formData.startDate,
                closingDate: formData.closingDate,
                planningDate: formData.planningDate,
                urgencyLevel: formData.urgencyLevel,
                campaignChoice: formData.campaignChoice,
                werfplaatsTeamConfig: formData.werfplaatsTeamConfig,
                teamRoles: formData.teamRoles,
                externalReviewers: formData.externalReviewers
            }
        };

        let actor: VacancyFlowActor | undefined = actorUser
            ? { ...actorUser, role: userRole !== undefined ? String(userRole) : undefined }
            : undefined;
        if (!actor?.id && !actor?.name) {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    actor = {
                        id: user.id,
                        name: (user.user_metadata?.full_name as string) || user.email || 'Gebruiker',
                        email: user.email || undefined,
                        role: userRole !== undefined ? String(userRole) : undefined,
                    };
                }
            } catch { /* ignore */ }
        }

        const prevSnap: VacancySnapshotForDiff = {
            title: vacancy.title,
            description: vacancy.description,
            data: { ...(vacancy.data as any) },
            opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
            eindklant_id: vacancy.eindklant_id ?? null,
            shared_with_users: vacancy.shared_with_users,
            shared_with_clients: vacancy.shared_with_clients,
            status: (vacancy as any).status ?? null,
        };
        const nextSnap: VacancySnapshotForDiff = {
            title: vacancy.title,
            description: vacancy.description,
            data: { ...(updates.data as any) },
            opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
            eindklant_id: vacancy.eindklant_id ?? null,
            shared_with_users: updates.shared_with_users,
            shared_with_clients: updates.shared_with_clients,
            status: (vacancy as any).status ?? null,
        };
        const summaries = describeVacancySnapshotChanges(prevSnap, nextSnap);
        if (summaries.length > 0) {
            updates.data = appendVacancyFlowStepHistory(updates.data as Record<string, unknown>, {
                kind: 'save',
                stepId: String(VacancyStep.STRATEGY),
                stepLabel: '4. Team voor Vacature',
                changeSummaries: summaries,
            }, actor) as typeof updates.data;
        }

        try {
            await supabase_update(vacancy.id, updates as any);
            onUpdate(updates as any);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSaving(false);
            if (proceed) {
                onNext();
            }
        }
    };

    const handleGenerateCompanyAI = async () => {
        if (!newCompanyForm.name) {
            alert("Vul eerst de bedrijfsnaam in.");
            return;
        }
        setIsGeneratingAi(true);
        try {
            const description = await generateCompanyDescription(newCompanyForm.name, newCompanyForm.profile || 'Algemeen', "werken bij, ambitie, vacature, cultuur");
            setNewCompanyForm(prev => ({ ...prev, description }));
        } catch (e) {
            console.error(e);
            alert("Fout bij genereren AI tekst.");
        } finally {
            setIsGeneratingAi(false);
        }
    };

    const handleSaveNewCompany = async () => {
        if (!newCompanyForm.name) {
            alert("Bedrijfsnaam is verplicht.");
            return;
        }
        setIsSavingNewCompany(true);
        try {
            const newClientData = {
                name: newCompanyForm.name,
                website: newCompanyForm.website,
                industry: newCompanyForm.profile,
                organizationDescription: newCompanyForm.description,
                client_type: 'Company'
            };
            const { data, error } = await supabase.from('clients').insert([newClientData]).select().single();
            if (error) throw error;
            if (data) {
                setClients(prev => [...prev, data]);
                handleChange('clientId', data.id);
                setShowNewCompanyModal(false);
                setNewCompanyForm({ name: '', website: '', profile: '', description: '' });
            }
        } catch (e: any) {
            console.error(e);
            alert("Mislukt: " + e.message);
        } finally {
            setIsSavingNewCompany(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn pb-20 relative">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Team & Campagne</h3>
                    <p className="text-xs text-slate-500 font-medium">Configureer de planning en kies je route.</p>
                </div>
                {!isReadOnly && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleSave(true)}
                            disabled={isSaving}
                            className="bg-[#e51b23] text-white px-5 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest shadow-md hover:bg-[#B71C1C] transition-all flex items-center gap-2 active:scale-95"
                        >
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 
                            Opslaan & Verder
                        </button>
                    </div>
                )}
            </div>

            <div className="w-full space-y-6 animate-fadeIn">
                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                        <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Stap 1</h2>
                        <h3 className="text-lg font-black text-slate-900 mb-6 tracking-tight">Klant & Team</h3>
                        
                        <div className="space-y-8">
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Werfplaats / Agencies Toewijzen</label>
                                </div>
                                <p className="text-sm text-slate-500 mb-4 max-w-2xl">Geef collega's of bureaus toegang tot deze vacature en bepaal hun rol en rechten in dit traject.</p>
                                
                                
                                <div className="animate-fadeIn bg-white border border-slate-200 rounded-xl p-4 shadow-sm mt-4">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                        <Users size={12} /> Team Samenstellen
                                    </h4>
                                {!isReadOnly && (
                                    <div className="relative z-50 mb-6" ref={teamDropdownRef}>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                            <input
                                                type="text"
                                                placeholder="Zoek collega of bureau om toe te voegen..."
                                                value={teamSearch}
                                                onChange={e => { setTeamSearch(e.target.value); setIsTeamDropdownOpen(true); }}
                                                onFocus={() => setIsTeamDropdownOpen(true)}
                                                className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] shadow-sm transition-all"
                                            />
                                        </div>
                                        {isTeamDropdownOpen && (teamSearch.trim() !== '' || colleagues.length > 0) && (
                                            <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto z-[60] p-2 space-y-1">
                                                {colleagues.filter(c => !formData.sharedWithUsers.includes(c.id) && (c.name?.toLowerCase().includes(teamSearch.toLowerCase()) || c.email?.toLowerCase().includes(teamSearch.toLowerCase()))).map(c => (
                                                    <div key={c.id} onClick={() => addUserToTeam(c.id, false)} className="flex items-center gap-3 p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-lg cursor-pointer transition-all">
                                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700">{c.name?.charAt(0) || c.email?.charAt(0)}</div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900">{c.name || c.email}</p>
                                                            <p className="text-[10px] text-slate-500 uppercase font-black">{c.role || 'Gebruiker'}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {/* Clients */}
                                                {clients.filter(c => ['Agency', 'Subcontractor'].includes(c.client_type) && !formData.sharedWithClients.includes(c.id) && c.name.toLowerCase().includes(teamSearch.toLowerCase())).map(c => (
                                                    <div key={c.id} onClick={() => addUserToTeam(c.id, true)} className="flex items-center gap-3 p-2 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-lg cursor-pointer transition-all">
                                                         <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-xs font-bold text-red-700">{c.name.charAt(0)}</div>
                                                         <div>
                                                             <p className="text-sm font-bold text-red-900">{c.name}</p>
                                                             <p className="text-[10px] text-red-500 uppercase font-black">Externe Agency</p>
                                                         </div>
                                                    </div>
                                                ))}
                                                {colleagues.filter(c => !formData.sharedWithUsers.includes(c.id)).length === 0 && teamSearch.length === 0 && (
                                                    <div className="p-4 text-center text-xs font-medium text-slate-500">Iedereen is al toegevoegd aan dit team.</div>
                                                )}
                                                {/* External Email */}
                                                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teamSearch) && !formData.externalReviewers.includes(teamSearch) && !colleagues.find(c => c.email === teamSearch) && (
                                                    <div onClick={() => addExternalReviewer(teamSearch)} className="flex items-center gap-3 p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-lg cursor-pointer transition-all">
                                                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-xs font-bold text-orange-700">@</div>
                                                        <div>
                                                            <p className="text-sm font-bold text-orange-900">{teamSearch}</p>
                                                            <p className="text-[10px] text-orange-500 uppercase font-black">+ Uitnodigen als Externe Reviewer</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {colleagues.length > 0 && formData.sharedWithUsers.length < colleagues.length && !isReadOnly && (
                                            <button 
                                                onClick={addAllColleagues}
                                                className="mt-2 text-[10px] font-black uppercase tracking-widest text-[#e51b23] hover:underline"
                                            >
                                                + Voeg het gehele bedrijf toe (als Recruiter)
                                            </button>
                                        )}
                                    </div>
                                )}
                                
                                <div className="space-y-3">
                                    {colleagues.filter(c => formData.sharedWithUsers.includes(c.id)).map(colleague => (
                                        <div key={colleague.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50 shadow-sm transition-all hover:border-slate-300">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-sm font-bold text-slate-700 border border-slate-200 shrink-0">
                                                    {colleague.name?.charAt(0) || colleague.email?.charAt(0)}
                                                </div>
                                                <div className="truncate">
                                                    <span className="text-sm font-black text-slate-900 block truncate">{colleague.name || colleague.email}</span>
                                                    <span className="text-xs text-slate-500 uppercase tracking-widest font-bold block truncate">{colleague.role || 'Gebruiker'}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 sm:w-64 shrink-0">
                                                <div className="relative flex-1">
                                                    <select 
                                                        value={formData.teamRoles[colleague.id] || 'Recruiter'}
                                                        onChange={e => handleChange('teamRoles', { ...formData.teamRoles, [colleague.id]: e.target.value })}
                                                        disabled={isReadOnly}
                                                        className="w-full bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded-lg pl-3 pr-8 py-2 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] appearance-none cursor-pointer"
                                                    >
                                                        {VACANCY_ROLES.map(r => (
                                                            <option key={r.id} value={r.id}>{r.id} ({r.desc})</option>
                                                        ))}
                                                    </select>
                                                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-slate-400">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                                    </div>
                                                </div>
                                                {!isReadOnly && (
                                                    <button onClick={() => removeUserFromTeam(colleague.id, false)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-red-100 shadow-sm">
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {clients.filter(c => ['Agency', 'Subcontractor'].includes(c.client_type) && formData.sharedWithClients.includes(c.id)).map(agency => (
                                        <div key={agency.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-[#e51b23] bg-red-50/20 shadow-sm transition-all">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-xl bg-red-100 shadow-sm flex items-center justify-center text-sm font-bold text-red-700 border border-red-200 shrink-0">
                                                    {agency.name?.charAt(0)}
                                                </div>
                                                <div className="truncate">
                                                    <span className="text-sm font-black text-slate-900 block truncate">{agency.name}</span>
                                                    <span className="text-xs text-slate-500 uppercase tracking-widest font-bold block truncate">Externe Agency Toegang</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 sm:w-64 shrink-0 justify-end">
                                                <span className="text-[10px] font-black uppercase text-red-600 bg-red-100 px-3 py-1.5 rounded-lg border border-red-200">Volledige Toegang</span>
                                                {!isReadOnly && (
                                                    <button onClick={() => removeUserFromTeam(agency.id, true)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-red-100 shadow-sm">
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {formData.externalReviewers.map((email: string) => (
                                        <div key={email} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-orange-200 bg-orange-50/30 shadow-sm transition-all hover:border-orange-300">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-xl bg-orange-100 shadow-sm flex items-center justify-center text-sm font-bold text-orange-700 border border-orange-200 shrink-0">
                                                    @
                                                </div>
                                                <div className="truncate">
                                                    <span className="text-sm font-black text-slate-900 block truncate">{email}</span>
                                                    <span className="text-xs text-orange-500 uppercase tracking-widest font-bold block truncate">Externe Reviewer (Klantportaal)</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 sm:w-64 shrink-0 justify-end">
                                                <span className="text-[10px] font-black uppercase text-orange-600 bg-orange-100 px-3 py-1.5 rounded-lg border border-orange-200">Reviewer Login Code</span>
                                                {!isReadOnly && (
                                                    <button onClick={() => removeExternalReviewer(email)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-red-100 shadow-sm">
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {formData.sharedWithUsers.length === 0 && formData.sharedWithClients.length === 0 && formData.externalReviewers.length === 0 && (
                                        <div className="p-4 text-center text-xs font-medium text-slate-500 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                                            Nog niemand geselecteerd.
                                        </div>
                                    )}
                                </div> {/* Close the new team card */}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                        <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Stap 2</h2>
                        <h3 className="text-lg font-black text-slate-900 mb-6 tracking-tight">Planning & Tijdlijn</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Start Werving</label>
                                <DatePicker
                                    locale="nl"
                                    dateFormat="d MMMM yyyy HH:mm"
                                    timeFormat="HH:mm"
                                    showTimeSelect
                                    selected={parseDateString(formData.planningDate || formData.startDate)}
                                    onChange={(date: Date | null) => handleChange('planningDate', formatDateTimeString(date))}
                                    placeholderText="Kies een startdatum en tijd"
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:ring-2 focus:ring-[#e51b23] outline-none transition-all cursor-pointer"
                                    wrapperClassName="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Tot / Deadline</label>
                                <DatePicker
                                    locale="nl"
                                    dateFormat="d MMMM yyyy HH:mm"
                                    timeFormat="HH:mm"
                                    showTimeSelect
                                    selected={parseDateString(formData.closingDate)}
                                    onChange={(date: Date | null) => handleChange('closingDate', formatDateTimeString(date))}
                                    placeholderText="Kies een deadline"
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:ring-2 focus:ring-[#e51b23] outline-none transition-all cursor-pointer"
                                    wrapperClassName="w-full"
                                />
                            </div>
                        </div>
                    </section>

                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                        <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Stap 3</h2>
                        <h3 className="text-lg font-black text-slate-900 mb-6 tracking-tight">Kies je route</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <button
                                onClick={() => handleChange('campaignChoice', 'werfplaats')}
                                className={`relative p-6 rounded-2xl border-2 text-left transition-all overflow-hidden group ${formData.campaignChoice === 'werfplaats' ? 'border-[#e51b23] bg-red-50/10' : 'border-slate-200 bg-white hover:border-red-200'}`}
                            >
                                {formData.campaignChoice === 'werfplaats' && <div className="absolute top-4 right-4 text-[#e51b23]"><Check size={20} /></div>}
                                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-[#e51b23] mb-4"><Sparkles size={20} /></div>
                                <h4 className="text-lg font-black text-slate-900 mb-1">Jouw Werfplaats Team</h4>
                                <p className="text-xs font-medium text-slate-500">Volledig ontzorgd door specialisten. Wij regelen de leads, pre-screening en campagnes.</p>
                            </button>
                            
                            <button
                                onClick={() => handleChange('campaignChoice', 'zelf_doen')}
                                className={`relative p-6 rounded-2xl border-2 text-left transition-all overflow-hidden group ${formData.campaignChoice === 'zelf_doen' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            >
                                {formData.campaignChoice === 'zelf_doen' && <div className="absolute top-4 right-4 text-slate-900"><Check size={20} /></div>}
                                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700 mb-4"><TrendingUp size={20} /></div>
                                <h4 className="text-lg font-black text-slate-900 mb-1">Zelf Doen</h4>
                                <p className="text-xs font-medium text-slate-500">Zet zelf je campagnes op via premium jobboards en beheer het gehele proces zelf.</p>
                            </button>
                        </div>

                        {formData.campaignChoice === 'werfplaats' && (
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 relative overflow-hidden mt-6 mb-2 text-slate-900">
                                
                                <h4 className="text-base font-black mb-1 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-[#e51b23]">
                                        <Briefcase size={14} />
                                    </div>
                                    Werfplaats Recruitment
                                </h4>
                                <p className="text-slate-500 text-xs font-medium mb-6">Neem direct contact op met onze recruiters om het proces succesvol af te trappen.</p>

                                <div className="space-y-6 relative z-10">
                                    <div className="flex flex-col items-start gap-4">
                                        
                                        {contactSuccess ? (
                                            <div className="w-full bg-emerald-50 text-emerald-700 border border-emerald-200 p-6 rounded-xl flex flex-col justify-center items-center text-center animate-fadeIn">
                                                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                                                    <Check size={20} className="text-emerald-500" />
                                                </div>
                                                <h5 className="text-base font-black text-emerald-900 mb-1">Verzoek succesvol ontvangen!</h5>
                                                <p className="font-medium text-xs max-w-sm">We hebben je verzoek binnengekregen. {contactSuccess === 'whatsapp' ? 'Check je WhatsApp, er is een link geopend.' : contactSuccess === 'schedule' ? 'We sturen je zo een uitnodiging.' : 'Thom belt je zo snel mogelijk op.'}</p>
                                                <button onClick={() => setContactSuccess(null)} className="mt-4 text-xs font-bold text-emerald-600 hover:text-emerald-500 underline underline-offset-4">Nog een vraag stellen</button>
                                            </div>
                                        ) : (
                                            <div className="w-full flex flex-col sm:flex-row gap-3">
                                                <button 
                                                    onClick={() => handleContactWerfplaats('whatsapp')}
                                                    className="flex-1 bg-[#F1FDF7] text-[#007B55] border border-[#BDE8D3] hover:bg-[#E2F8EE] px-4 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 active:scale-95 group"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg> WhatsApp
                                                </button>
                                                <button 
                                                    onClick={() => handleContactWerfplaats('schedule')}
                                                    className="flex-1 bg-[#e51b23] text-white hover:bg-[#c2151c] px-4 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-sm transition-all flex items-center justify-center gap-2 active:scale-95 group"
                                                >
                                                    <Calendar size={16} /> Plan Afspraak
                                                </button>
                                                <button 
                                                    onClick={() => handleContactWerfplaats('call')}
                                                    className="flex-1 bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 px-4 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 active:scale-95 group"
                                                >
                                                    <Phone size={16} /> We bellen je op
                                                </button>
                                            </div>
                                        )}
                                        
                                        <div className="w-full mt-4 flex justify-end">
                                            <button 
                                                onClick={() => {
                                                    handleChange('askWerfplaats', true);
                                                    handleSave(true);
                                                }}
                                                className="bg-[#e51b23] hover:bg-[#c2151c] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-sm transition-all active:scale-95"
                                            >
                                                Opslaan & Voltooien
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {formData.campaignChoice === 'zelf_doen' && (
                            <div className="p-8 text-center border border-slate-200 rounded-2xl bg-slate-50 mt-6 mb-2">
                               <TrendingUp size={24} className="mx-auto text-slate-400 mb-3" />
                               <h4 className="text-base font-black text-slate-900 mb-2">Je beheert de campagne zelf</h4>
                               <p className="text-xs font-medium text-slate-500 max-w-sm mx-auto">Sla op en ga naar de volgende stap om de Premium Job Boards en netwerkkanalen te selecteren waarmee je wilt adverteren.</p>
                               <button onClick={() => handleSave(true)} className="mt-6 bg-[#e51b23] hover:bg-[#c2151c] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-sm transition-all active:scale-95">
                                   Opslaan & Voltooien
                               </button>
                            </div>
                        )}

                    </section>
            </div>

            {showScheduler && (
                <SupportSchedulerModal
                    onClose={() => setShowScheduler(false)}
                />
            )}

            {/* NEW COMPANY MODAL */}
            {showNewCompanyModal && (
                <div className="fixed inset-0 z-[200] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden flex flex-col animate-scaleIn">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                                    <Building2 className="text-[#e51b23]" size={20} />
                                    Nieuwe Organisatie Toevoegen
                                </h3>
                                <p className="text-xs text-slate-500 font-medium">Voeg direct een nieuwe klant toe aan de actieve database.</p>
                            </div>
                            <button onClick={() => setShowNewCompanyModal(false)} className="text-slate-400 hover:text-red-500 transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Bedrijfsnaam *</label>
                                    <input 
                                        type="text" 
                                        value={newCompanyForm.name} 
                                        onChange={e => setNewCompanyForm({...newCompanyForm, name: e.target.value})}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-[#e51b23]"
                                        placeholder="Bijv. Tech Innovators B.V."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Website</label>
                                    <input 
                                        type="text" 
                                        value={newCompanyForm.website} 
                                        onChange={e => setNewCompanyForm({...newCompanyForm, website: e.target.value})}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-[#e51b23]"
                                        placeholder="https://..."
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Profiel / Industrie</label>
                                <input 
                                    type="text" 
                                    value={newCompanyForm.profile} 
                                    onChange={e => setNewCompanyForm({...newCompanyForm, profile: e.target.value})}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-[#e51b23]"
                                    placeholder="Bijv. Fintech, Healthcare, E-commerce..."
                                />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Bedrijfsomschrijving (Voor Vacature)</label>
                                    <button 
                                        onClick={handleGenerateCompanyAI}
                                        disabled={isGeneratingAi || !newCompanyForm.name}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                                    >
                                        {isGeneratingAi ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                        Genereer met AI
                                    </button>
                                </div>
                                <textarea 
                                    value={newCompanyForm.description} 
                                    onChange={e => setNewCompanyForm({...newCompanyForm, description: e.target.value})}
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm leading-relaxed outline-none focus:border-[#e51b23] min-h-[150px] resize-y"
                                    placeholder="Typ zelf een wervende tekst of gebruik de paarse AI knop om er één te genereren op basis van de bedrijfsnaam..."
                                />
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
                            <button onClick={() => setShowNewCompanyModal(false)} className="px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-200 rounded-xl transition-colors">Annuleren</button>
                            <button 
                                onClick={handleSaveNewCompany}
                                disabled={isSavingNewCompany || !newCompanyForm.name}
                                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black shadow-lg shadow-emerald-200 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                            >
                                {isSavingNewCompany ? <Loader2 size={18} className="animate-spin" /> : "Klant Toevoegen"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VacancyTeamChoice;
